import type { QueryKey } from "@tanstack/react-query";

import { mountayaKeys } from "./mountayaKeys.js";

/**
 * The cache keys of the catalogs a studio cannot draw without: the activity taxonomy and the
 * tile-layer manifest.
 *
 * `activitiesQueryOptions` and `layersQueryOptions` read under them, and they hold no React, so
 * `@mountaya/client-react/query` publishes them to a server that seeds or prefetches a catalog.
 */

/**
 * ACTIVITIES_SCOPE is the cache scope the catalog is read under.
 *
 * A constant rather than the caller's credential scope, and that is the point: the activity
 * taxonomy is the same list for every credential. Keying it per credential would refetch the
 * identical document on every organization switch and on every share-token rotation, which is
 * a network round trip to learn nothing changed.
 */
const ACTIVITIES_SCOPE = "catalog";

/**
 * activitiesQueryKey is the catalog's cache key, published rather than left implicit.
 *
 * A caller reading the catalog OUT of the cache (a render-free rule, a tool handler, a test
 * seeding a fixture) reaches for `getQueryData(key)`, and `getQueryData` answers `undefined`
 * for a key that does not match rather than failing. So a key spelled at the call site is a
 * reader that goes quietly empty the day the key gains a segment, and the surface that
 * depended on it degrades instead of breaking. Naming it here is what makes the two halves
 * one decision.
 *
 * A FUNCTION taking nothing, beside {@link layersQueryKey}, which takes a credential scope. The
 * empty parameter list is the statement: both catalogs are read the same way, and this one needs
 * no scope because the taxonomy is the same list for every credential, not by oversight. A
 * constant here would read as if the other builder had a parameter this one had forgotten.
 */
export const activitiesQueryKey = (): QueryKey =>
  mountayaKeys.operation(ACTIVITIES_SCOPE, "rest", "getActivities");

/**
 * layersQueryKey is the manifest's cache key for one credential scope.
 *
 * It is scoped where the activity catalog's deliberately is NOT, and the difference is the
 * whole point: the taxonomy is the same list for everyone, while the manifest is narrowed by
 * the API to what the CALLER may draw: an organization's terrain and overlay allowlists, and
 * its agreement. A page that mounts both an embed preview and the signed-in studio holds two
 * credentials at once, so an unscoped key would serve whichever mounted first to both, and
 * the preview would show the publisher's own layers rather than a visitor's.
 *
 * Exported as a factory rather than a constant because the scope is the argument: a caller
 * reading the manifest OUT of the cache needs the same key the hook wrote under, and
 * `getQueryData` answers `undefined` to a near-miss rather than failing.
 *
 * The studio scope the manifest is read for narrows it again: the API answers the overlays of
 * the organization `organization_id` names. A named scope APPENDS to the key rather than
 * replacing it, so the key of the read that names none is a prefix of every scope's, and one
 * invalidation of it drops them all.
 *
 * @param credentialScope The scope of the client that reads the manifest, its
 *   `transport.credentialScope`.
 * @param organizationId The organization the manifest is read for, or nothing for the read
 *   that names none. An empty string names none.
 */
export const layersQueryKey = (credentialScope: string, organizationId?: string): QueryKey =>
  organizationId === undefined || organizationId === ""
    ? mountayaKeys.operation(credentialScope, "tiles", "getTileLayers")
    : mountayaKeys.operation(credentialScope, "tiles", "getTileLayers", {
        query: { organization_id: organizationId },
      });
